import { useState } from 'react'
import { useAddons, useCreateAddon, useUpdateAddon, useToggleAddon } from '@/hooks/useAddons'
import { usePlans } from '@/hooks/usePlans'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { StatusBadge } from '@/components/common/StatusBadge'
import { Skeleton } from '@/components/ui/skeleton'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Addon, AddonCreateRequest, AddonUpdateRequest, AddonEffect } from '@/types'
import { formatCurrency, getApiErrorMessage } from '@/lib/utils'
import { toast } from 'sonner'
import { Plus, Pencil, Puzzle, ArrowUpCircle, Unlock, Infinity } from 'lucide-react'

const EFFECT_TYPES = [
  { value: 'increase_limit', label: 'Aumentar limite' },
  { value: 'unlock_feature', label: 'Desbloquear funcion' },
  { value: 'set_unlimited', label: 'Establecer ilimitado' },
]

const effectIcon = (type: string) => {
  switch (type) {
    case 'increase_limit':
      return <ArrowUpCircle className="h-4 w-4 text-blue-500" />
    case 'unlock_feature':
      return <Unlock className="h-4 w-4 text-green-500" />
    case 'set_unlimited':
      return <Infinity className="h-4 w-4 text-purple-500" />
    default:
      return <Puzzle className="h-4 w-4 text-muted-foreground" />
  }
}

const emptyEffect: AddonEffect = { type: 'increase_limit', target: '', value: 0 }

const emptyCreateForm: AddonCreateRequest = {
  addon_id: '',
  name: '',
  description: '',
  price_monthly_usd: 0,
  effect: { ...emptyEffect },
  available_for_plans: [],
  sort_order: 0,
}

export const AddonsListPage = () => {
  const { data: addons, isLoading } = useAddons()
  const { data: plansData } = usePlans()
  const createAddon = useCreateAddon()
  const updateAddon = useUpdateAddon()
  const toggleAddon = useToggleAddon()

  const planOptions = (plansData ?? []).map((p: { name: string }) => p.name)

  const [createOpen, setCreateOpen] = useState(false)
  const [createForm, setCreateForm] = useState<AddonCreateRequest>({ ...emptyCreateForm })
  const [editAddon, setEditAddon] = useState<Addon | null>(null)
  const [editForm, setEditForm] = useState<AddonUpdateRequest>({})

  const openCreate = () => {
    setCreateForm({ ...emptyCreateForm, effect: { ...emptyEffect }, available_for_plans: [] })
    setCreateOpen(true)
  }

  const openEdit = (addon: Addon) => {
    setEditForm({
      name: addon.name,
      description: addon.description,
      price_monthly_usd: addon.price_monthly_usd,
      effect: { ...addon.effect },
      available_for_plans: [...addon.available_for_plans],
      sort_order: addon.sort_order,
    })
    setEditAddon(addon)
  }

  const handleCreate = async () => {
    try {
      await createAddon.mutateAsync(createForm)
      setCreateOpen(false)
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Error al crear add-on'))
    }
  }

  const handleSave = async () => {
    if (!editAddon) return
    try {
      await updateAddon.mutateAsync({ id: editAddon.id, updates: editForm })
      setEditAddon(null)
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Error al actualizar add-on'))
    }
  }

  const togglePlan = (plans: string[], plan: string) => {
    return plans.includes(plan) ? plans.filter((p) => p !== plan) : [...plans, plan]
  }

  const renderEffectEditor = (effect: AddonEffect, onChange: (effect: AddonEffect) => void) => (
    <div className="space-y-3 rounded-lg border p-3">
      <Label className="text-sm font-medium">Efecto</Label>
      <div className="grid gap-3">
        <div className="grid gap-2">
          <Label className="text-xs text-muted-foreground">Tipo</Label>
          <Select value={effect.type} onValueChange={(v) => onChange({ ...effect, type: v })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EFFECT_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-2">
          <Label className="text-xs text-muted-foreground">Target</Label>
          <Input
            placeholder="keywords, pdf_reports_per_month..."
            value={effect.target}
            onChange={(e) => onChange({ ...effect, target: e.target.value })}
          />
        </div>
        <div className="grid gap-2">
          <Label className="text-xs text-muted-foreground">Valor</Label>
          <Input
            placeholder="10, true, unlimited..."
            value={String(effect.value ?? '')}
            onChange={(e) => {
              const raw = e.target.value
              const num = Number(raw)
              const val = raw === 'true' ? true : raw === 'false' ? false : isNaN(num) ? raw : num
              onChange({ ...effect, value: val })
            }}
          />
        </div>
      </div>
    </div>
  )

  const renderPlanCheckboxes = (selected: string[], onChange: (plans: string[]) => void) => (
    <div className="space-y-2">
      <Label>Disponible para planes</Label>
      <div className="flex flex-wrap gap-3">
        {planOptions.map((plan) => (
          <label key={plan} className="flex items-center gap-2 text-sm capitalize">
            <Checkbox
              checked={selected.includes(plan)}
              onCheckedChange={() => onChange(togglePlan(selected, plan))}
            />
            {plan}
          </label>
        ))}
      </div>
    </div>
  )

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold">Add-ons</h1>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-72" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Add-ons</h1>
        <Button onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Crear add-on
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {addons?.map((addon) => (
          <Card key={addon.id} className={!addon.is_active ? 'opacity-60' : ''}>
            <CardHeader className="flex flex-row items-start justify-between pb-2">
              <div>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Puzzle className="h-4 w-4 text-primary" />
                  {addon.name}
                </CardTitle>
                <p className="mt-1 font-mono text-xs text-muted-foreground">{addon.addon_id}</p>
              </div>
              <StatusBadge status={addon.is_active ? 'active' : 'inactive'} />
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="line-clamp-2 text-sm text-muted-foreground">
                {addon.description || 'Sin descripcion'}
              </p>

              <div className="text-sm">
                <p className="text-muted-foreground">Precio mensual</p>
                <p className="text-lg font-bold">{formatCurrency(addon.price_monthly_usd)}/mes</p>
              </div>

              <div className="space-y-1 text-sm">
                <p className="text-muted-foreground">Efecto</p>
                <div className="flex items-center gap-2 rounded bg-muted/50 px-2 py-1.5">
                  {effectIcon(addon.effect.type)}
                  <span className="font-mono text-xs">
                    {addon.effect.type}: {addon.effect.target} = {String(addon.effect.value)}
                  </span>
                </div>
              </div>

              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Planes</p>
                <div className="flex flex-wrap gap-1">
                  {addon.available_for_plans.map((plan) => (
                    <Badge key={plan} variant="default" className="text-xs capitalize">
                      {plan}
                    </Badge>
                  ))}
                  {addon.available_for_plans.length === 0 && (
                    <span className="text-xs text-muted-foreground">Ninguno</span>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between border-t pt-3">
                <span className="text-xs text-muted-foreground">Orden: {addon.sort_order}</span>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={addon.is_active}
                    onCheckedChange={() => toggleAddon.mutate(addon.id)}
                  />
                  <Button variant="ghost" size="sm" onClick={() => openEdit(addon)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {addons && addons.length === 0 && (
        <div className="py-12 text-center text-muted-foreground">No hay add-ons configurados</div>
      )}

      {/* Create Addon Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Crear add-on</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>ID (slug)</Label>
              <Input
                placeholder="extra_keywords"
                value={createForm.addon_id}
                onChange={(e) => setCreateForm((f) => ({ ...f, addon_id: e.target.value }))}
              />
            </div>
            <div className="grid gap-2">
              <Label>Nombre</Label>
              <Input
                placeholder="Keywords Extra"
                value={createForm.name}
                onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div className="grid gap-2">
              <Label>Descripcion</Label>
              <Input
                placeholder="Descripcion del add-on..."
                value={createForm.description}
                onChange={(e) => setCreateForm((f) => ({ ...f, description: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Precio mensual (USD)</Label>
                <Input
                  type="number"
                  min={0}
                  step={0.01}
                  value={createForm.price_monthly_usd}
                  onChange={(e) =>
                    setCreateForm((f) => ({ ...f, price_monthly_usd: Number(e.target.value) }))
                  }
                />
              </div>
              <div className="grid gap-2">
                <Label>Orden</Label>
                <Input
                  type="number"
                  min={0}
                  value={createForm.sort_order}
                  onChange={(e) =>
                    setCreateForm((f) => ({ ...f, sort_order: Number(e.target.value) }))
                  }
                />
              </div>
            </div>
            {renderEffectEditor(createForm.effect, (effect) =>
              setCreateForm((f) => ({ ...f, effect }))
            )}
            {renderPlanCheckboxes(createForm.available_for_plans, (plans) =>
              setCreateForm((f) => ({ ...f, available_for_plans: plans }))
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleCreate}
              disabled={createAddon.isPending || !createForm.addon_id || !createForm.name}
            >
              {createAddon.isPending ? 'Creando...' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Addon Dialog */}
      <Dialog open={!!editAddon} onOpenChange={(open) => !open && setEditAddon(null)}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar add-on: {editAddon?.name}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Nombre</Label>
              <Input
                value={editForm.name ?? ''}
                onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div className="grid gap-2">
              <Label>Descripcion</Label>
              <Input
                value={editForm.description ?? ''}
                onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Precio mensual (USD)</Label>
                <Input
                  type="number"
                  min={0}
                  step={0.01}
                  value={editForm.price_monthly_usd ?? 0}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, price_monthly_usd: Number(e.target.value) }))
                  }
                />
              </div>
              <div className="grid gap-2">
                <Label>Orden</Label>
                <Input
                  type="number"
                  min={0}
                  value={editForm.sort_order ?? 0}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, sort_order: Number(e.target.value) }))
                  }
                />
              </div>
            </div>
            {renderEffectEditor(editForm.effect ?? { ...emptyEffect }, (effect) =>
              setEditForm((f) => ({ ...f, effect }))
            )}
            {renderPlanCheckboxes(editForm.available_for_plans ?? [], (plans) =>
              setEditForm((f) => ({ ...f, available_for_plans: plans }))
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditAddon(null)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={updateAddon.isPending}>
              {updateAddon.isPending ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
